import { Dialog, Grid, MenuItem, Select, Typography } from '@material-ui/core';
import { MTableToolbar } from 'material-table';
import React, { ReactElement } from 'react';

import { RouteEventTypes, track } from '~/src/dash/analytics/analytics';
import { ResourceActiveDisabled, ResourceActiveEnabled } from '~/src/dash/api/console-api';
import {
  deleteInventoryRoute,
  getInventoryRoutesByPublisherID,
  IDestination,
  IInventoryRoute,
  updateInventoryRoute,
} from '~/src/dash/api/inventory_routes';
import {
  IInventoryRouteRowData,
  IListTableColumn,
  ListTable,
} from '~/src/dash/components/list-table';
import { ModalSelectTable } from '~/src/dash/components/modal-select-table';
import ModelMissingPermission from '~/src/dash/components/model-missing-permission';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteChannelExisting,
  RouteInventoryRouteEdit,
  RouteInventoryRouteNew,
} from '~/src/dash/router/routes';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { checkUserInfoMasked, hasRoleSuperUser } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import { ButtonView } from '~/src/dash/views/button-view';
import { RowButton } from '~/src/dash/views/component-view';
import { IInventoryRouteInfo } from '~/src/dash/views/creatives/creative-library-list-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState extends IBaseState {
  loading: boolean;
  updating: boolean;
  snackbarMessage: string;
  snackbarError: boolean;
  activeFilter: string;
  Routes: IInventoryRoute[];
  Destinations: IDestination[];
  openDestinationsModal: boolean;
}

export const RouteSettingsListView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: () => ({
      toolbarRow: {
        marginLeft: -10,
        width: '100%',
        marginTop: 5,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      updating: false,
      loading: false,
      snackbarMessage: '',
      snackbarError: false,
      activeFilter: 'Active',
      page: 0,
      rowsPerPage: 25,
      Routes: [] as IInventoryRoute[],
      Destinations: [] as IDestination[],
      openDestinationsModal: false,
    };

    componentDidMount() {
      this.trackEvent('Route List - View');
      this.getRoutes();
    }

    trackEvent = (
      eventTitle: RouteEventTypes,
      time_to_load_sec = 0,
      rows_per_page = 0,
      filter = '',
    ): void => {
      const is_super_user = hasRoleSuperUser(this.props.user.user);
      if (['Route List - Channels List Loaded'].includes(eventTitle)) {
        track(
          eventTitle,
          {
            is_super_user,
            time_to_load_sec,
          },
          this.props.user.user,
        );
      } else if (eventTitle === 'Route List - Change Rows Per Page') {
        track(
          eventTitle,
          {
            is_super_user,
            rows_per_page,
          },
          this.props.user.user,
        );
      } else if (eventTitle === 'Route List - Use Status Filter') {
        track(
          eventTitle,
          {
            is_super_user,
            filter,
          },
          this.props.user.user,
        );
      } else {
        track(
          eventTitle,
          {
            is_super_user,
          },
          this.props.user.user,
        );
      }
    };

    getRoutes = () => {
      getInventoryRoutesByPublisherID(this.props.session.publisher.ID)
        .then((maps: IInventoryRoute[]) => {
          const Routes = maps.map((Route: IInventoryRoute) => {
            // mask the site name to the route routes
            Route.Destinations = Route.Destinations.map((destination: IDestination) => {
              destination.Name = checkUserInfoMasked(this.props.user.user)
                ? `demo_destination_${destination.ID}`
                : destination.Name;
              return destination;
            });

            // mask the site name to the route
            return Route;
          });

          this.setState({
            Routes,
          });
        })
        .catch(() => {
          this.setState({
            snackbarError: true,
            snackbarMessage: 'Error loading routes',
          });
        });
    };

    getChannelSettingsRouteByID = (id: number): string => {
      return getRoutePath(RouteChannelExisting, extractRouteParams(this.props), {
        ch: id.toString(),
      });
    };

    getRouteSettingsRoute = (id: number): string => {
      return getRoutePath(RouteInventoryRouteEdit, extractRouteParams(this.props), {
        inventoryrouteid: id.toString(),
      });
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    // ************************************
    // ****** CRUD RELATED FUNCTIONS ******
    // ************************************

    createRoute = () => {
      this.trackEvent('Route List - Create Route');
      navigate(RouteInventoryRouteNew, extractRouteParams(this.props));
    };

    updateRoute = (itemID: string, checked: boolean) => {
      if (this.state.updating) {
        return;
      }
      const Routes = [...this.state.Routes];
      let selectedIndex = -1;
      // find the network to be updated
      for (let i = 0; i < Routes.length; i++) {
        if (Routes[i].ID.toString() === itemID) {
          Routes[i].Active = checked ? ResourceActiveEnabled : ResourceActiveDisabled;
          selectedIndex = i;
          break;
        }
      }
      // create an Route with the fields that need to be updated as well as the ID
      if (selectedIndex >= 0) {
        const Route = {
          ID: Routes[selectedIndex].ID,
          Active: Routes[selectedIndex].Active,
        } as IInventoryRoute;
        this.setState(
          {
            updating: true,
          },
          () => {
            // this is not full update (false) since we only want to update the active field
            updateInventoryRoute(Route, this.props.session.publisher.ID)
              .then(() => {
                this.setState({
                  Routes,
                  snackbarMessage: 'Successfully saved route.',
                  snackbarError: false,
                  updating: false,
                });
              })
              .catch(() => {
                this.setState({
                  snackbarMessage: 'Failed to save route.',
                  snackbarError: true,
                  updating: false,
                });
              });
          },
        );
      }
    };

    deleteRoute = (RouteID: string) => {
      this.setState(
        {
          updating: true,
        },
        () => {
          deleteInventoryRoute(RouteID, this.props.session.publisher.ID)
            .then(() => {
              const Routes = this.state.Routes.filter(
                (Route: IInventoryRoute) => Route.ID.toString() !== RouteID,
              );
              this.setState({
                Routes,
                snackbarMessage: 'Successfully deleted route',
                snackbarError: false,
                updating: false,
              });
            })
            .catch((err) => {
              console.error(err);
              this.setState({
                snackbarMessage: 'Error while deleting route',
                snackbarError: true,
                updating: false,
              });
            });
        },
      );
      // the delete modal expects a promise to be returned
      return new Promise((resolve) => resolve(undefined));
    };

    // ************************************
    // ******** HANDLER FUNCTIONS *********
    // ************************************
    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      if (target.name === 'activeFilter') {
        this.trackEvent('Route List - Use Status Filter', 0, 0, target.value);
        this.setState({
          [target.name]: target.value,
          page: 0,
        });
      } else {
        this.setState({ [target.name]: target.value });
      }
    };

    handleSetUpdating = (updating: boolean) => {
      this.setState({ updating });
    };

    handleOpenDestinationsModal = (mapID: string) => {
      const { Routes } = this.state;
      const foundMap = Routes.find((map: IInventoryRoute) => map.ID.toString() === mapID);
      if (foundMap) {
        this.setState({
          openDestinationsModal: true,
          Destinations: foundMap.Destinations,
        });
      }
    };

    // ************************************
    // ********* RENDER FUNCTIONS *********
    // ************************************

    // renderCustomRouteToolbar renders the route toolbar at the top of the list table
    renderCustomRouteToolbar = (props: any): ReactElement => {
      const { classes } = this.props;
      const { updating, activeFilter } = this.state;

      const copyProps = {
        ...props,
        searchFieldStyle: { ...props.searchFieldStyle, marginLeft: '-48px', width: '300px' },
      };

      return (
        <Grid item className={classes.toolbarRow}>
          <Grid container direction='row' justify='space-between' alignItems='center'>
            {/* grid with MTableToolbarWrapper class is required for auto focusing search upon loading the list table component */}
            <Grid item>
              <Grid container spacing={2} alignItems='center'>
                <Grid
                  item
                  onClick={() => this.trackEvent('Route List - Search Routes')}
                  className='MTableToolbarWrapper'
                >
                  <MTableToolbar {...copyProps} />
                </Grid>
                <Grid item>
                  <Typography style={{ fontSize: 14, paddingLeft: 30 }}>Status</Typography>
                </Grid>
                <Grid item>
                  <Select
                    name='activeFilter'
                    value={activeFilter}
                    onChange={this.handleChange}
                    style={{ fontSize: '14px' }}
                  >
                    <MenuItem value={'Active'}>Active</MenuItem>
                    <MenuItem value={'Inactive'}>Inactive</MenuItem>
                    <MenuItem value={'Show All'}>Show All</MenuItem>
                  </Select>
                </Grid>
              </Grid>
            </Grid>
            <RowButton
              right={
                <ButtonView
                  onClick={() => {
                    this.trackEvent('Route List - Create Route');
                    this.createRoute();
                  }}
                  text='+ New Route'
                  active={updating}
                />
              }
            />
          </Grid>
        </Grid>
      );
    };

    renderList = (): ReactElement => {
      const { user } = this.props;
      const { Routes, activeFilter } = this.state;

      const listTableColumns: IListTableColumn[] = [
        { field: 'Active', title: 'Active', renderType: 'activeswitch', searchable: true },
        { field: 'Name', title: 'Name', renderType: 'namelink', searchable: true }, // links to mapping form
        {
          field: 'ID',
          title: 'ID',
          renderType: 'normal',
          searchable: true,
          hidden: true,
          sorting: false,
        },
        { field: 'Destinations', title: 'Destinations', renderType: 'modal' },
        { field: 'Actions', title: 'Actions', renderType: 'delete', searchable: false },
      ];

      let listTableData: IInventoryRouteRowData[] = Routes.map((Route: IInventoryRoute) => {
        return {
          Active: Route.Active === ResourceActiveEnabled,
          ID: Route.ID.toString(),
          Name: checkUserInfoMasked(user.user)
            ? `demo_inventory_route_${Route.ID}`
            : `${Route.Name} [${Route.ID}]`,
          Destinations: Route.Destinations.length,
        } as IInventoryRouteRowData;
      });

      listTableData = listTableData.filter((rowData: IInventoryRouteRowData) => {
        if (activeFilter === 'Active') {
          return rowData.Active;
        } else if (activeFilter === 'Inactive') {
          return !rowData.Active;
        } else {
          return true;
        }
      });

      return (
        <Grid item style={{ width: '100%' }}>
          <ListTable
            title={''}
            columns={listTableColumns}
            data={listTableData}
            renderCustomToolbar={this.renderCustomRouteToolbar}
            handleSetUpdating={this.handleSetUpdating}
            getItemSettingRoute={this.getRouteSettingsRoute}
            getItemSettingRoute2={this.getChannelSettingsRouteByID}
            handleOpenModal={this.handleOpenDestinationsModal}
            handleChangeActive={this.updateRoute}
            handleDeleteItem={this.deleteRoute}
            fixedEnabled={true}
            pageSize={10}
            readRoleName={'read_channel'}
            writeRoleName={'write_channel'}
          />
        </Grid>
      );
    };

    render() {
      // NOTE: We destructure props and states used in render functions to clearly
      // show what we are passing as well as keeping the code clean and easier to read
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      const { Destinations, openDestinationsModal, snackbarError, snackbarMessage } = this.state;

      const readAuthorization = IS_AUTHORIZED(['read_channel'], user.user);
      const writeAuthorization = IS_AUTHORIZED(['write_channel'], user.user);
      if (!readAuthorization.isAuthorized && !writeAuthorization.isAuthorized) {
        return (
          <ModelMissingPermission
            missingPermissions={[
              ...readAuthorization.missingPermissions,
              ...writeAuthorization.missingPermissions,
            ]}
          />
        );
      }

      const DestinationsModal = Destinations.map((destination: IDestination) => {
        return {
          ID: destination.SiteID,
          Name: destination.Name,
          VastURL: destination.VastURL,
          DisableTooltip: destination.SiteID === undefined,
        };
      });

      const vastURLColumn = {
        title: 'Vast URL',
        field: 'VastURL',
        searchable: true,
        render: (rowData: IInventoryRouteInfo) => {
          return (
            <Typography
              style={{
                fontSize: '0.875rem',
                fontWeight: 400,
              }}
            >
              {rowData.VastURL || ''}
            </Typography>
          );
        },
      };

      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            {this.renderList()}
            <Dialog
              fullWidth
              maxWidth='md'
              keepMounted
              open={openDestinationsModal}
              onClose={() => this.setState({ openDestinationsModal: false })}
            >
              <ModalSelectTable
                title='Destinations'
                items={DestinationsModal}
                customColumns={[vastURLColumn]}
                onSelectRow={(siteID: string) => {
                  if (siteID) {
                    this.setState(
                      {
                        openDestinationsModal: false,
                      },
                      () => {
                        navigate(RouteChannelExisting, extractRouteParams(this.props), {
                          ch: siteID,
                        });
                      },
                    );
                  }
                }}
                interactive={true}
                width='400px'
              />
            </Dialog>

            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.snackbarClose}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
